import { createSlice } from '@reduxjs/toolkit';
import { areDatesEqual } from '../helpers';
import { createMood, getMoods } from './actions';
import { AveragePerDayMood, Mood } from './model';
import { GetMoodsCallType } from './model/GetMoodsCallType';

export interface MoodTrackerState {
  moods: Mood[];
  profileMoods: Mood[];
  averagePerDayForMoods: AveragePerDayMood[];
  loadingMoods: boolean;
  loadingCreateMood: boolean;
  pendingMood: Mood | null;
}

const initialState: MoodTrackerState = {
  moods: [],
  profileMoods: [],
  averagePerDayForMoods: [],
  loadingMoods: false,
  loadingCreateMood: false,
  pendingMood: null,
};

const moodTrackerSlice = createSlice({
  name: 'moodTracker',
  initialState,
  reducers: {
    clearMoods: (state) => {
      state.moods = [];
    },
    setPendingMood: (state, action) => {
      state.pendingMood = action.payload;
    },
    clearPendingMood: (state) => {
      state.pendingMood = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getMoods.pending, (state) => {
        state.loadingMoods = true;
      })
      .addCase(getMoods.fulfilled, (state, action) => {
        state.loadingMoods = false;

        if (action.payload) {
          if (action.meta.arg.type === GetMoodsCallType.chart) {
            state.moods = action.payload.data;
            state.averagePerDayForMoods = action.payload.averagePerDay;
          } else {
            state.profileMoods = action.payload.data;
          }
        } else {
          state.moods = [];
          state.profileMoods = [];
          state.averagePerDayForMoods = [];
        }
      })
      .addCase(getMoods.rejected, (state) => {
        state.loadingMoods = false;
      })
      .addCase(createMood.pending, (state, action) => {
        state.loadingCreateMood = true;
        // Optimistic update: add the pending mood to state
        const pendingMood: Mood = {
          value: action.meta.arg.value,
          date: action.meta.arg.date,
          timeRange: 0, // Default timeRange since it's not in CreateMoodDto
        };
        state.pendingMood = pendingMood;
      })
      .addCase(createMood.fulfilled, (state, action) => {
        const sortedMoods = state.moods.filter((mood) =>
          action.payload.find(
            (updatedMood) =>
              areDatesEqual(updatedMood.date, mood.date) &&
              mood.timeRange === updatedMood.timeRange
          )
            ? false
            : true
        );
        state.profileMoods = action.payload;
        state.moods = [...action.payload, ...sortedMoods];
        state.loadingCreateMood = false;
        state.pendingMood = null; // Clear pending mood on success
      })
      .addCase(createMood.rejected, (state) => {
        state.loadingCreateMood = false;
        state.pendingMood = null; // Clear pending mood on failure
      });
  },
});

export const { clearMoods, setPendingMood, clearPendingMood } =
  moodTrackerSlice.actions;

export default moodTrackerSlice.reducer;
